import { App } from '../state/app.actions';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const DEFAULT_LANDING_ROUTE = '/transactions/index';

/**
 * Guards the login route: if the session is already authenticated, redirect
 * to returnUrl (or the default landing route) instead of showing the login
 * form again. Waits on the same session-initialization dispatch AuthGuard
 * uses, so it never redirects away from /login while status is still
 * 'checking'.
 */
@Injectable({ providedIn: 'root' })
export class NoAuthGuard {
  constructor(
    private router: Router,
    private store: Store
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.store.dispatch(new App.Actions.InitializeSession()).pipe(
      map(() => {
        const authStatus = this.store.snapshot().application.authStatus;

        if (authStatus !== 'authenticated') {
          return true;
        }

        const returnUrl = route.queryParams?.['returnUrl'];
        const target = isSafeReturnUrl(returnUrl) ? returnUrl : DEFAULT_LANDING_ROUTE;
        return this.router.parseUrl(target);
      })
    );
  }
}

/**
 * A safe returnUrl must be an internal, relative path — never an
 * absolute/protocol-relative URL, which could be used for an open redirect.
 */
export function isSafeReturnUrl(url: unknown): url is string {
  return (
    typeof url === 'string' &&
    url.length > 0 &&
    url.startsWith('/') &&
    !url.startsWith('//') &&
    !url.includes('://')
  );
}
